import { useState } from "react";
import styled from "styled-components";
import Colors from '../../ui/global/Colors';
import Fonts from '../../ui/global/Fonts';
import Logo from "../../ui/components/Logo";
import Button from "../../ui/components/Button";
import Modal from "../../ui/components/Modal";
import PriceOptionsMini from "./PriceOptionsMini";
import { CREDITS_CONSTANTS } from "../../backend/useCredits";
import { useUser } from "../../backend/useUser";
import { useNavigate, useLocation } from "react-router-dom";
import { useConfig } from "../../utils/config";
import { useCreditsContext } from "./CreditsContext";
import {
  redirectToSignup as redirectToSignupHelper,
  onClickHobby as onClickHobbyHelper,
  onClickCreator as onClickCreatorHelper,
  getHobbyPriceText,
  getCreatorPriceText,
  getModalButtonText,
} from "./priceHelpers";
import { useMediaQuery } from "@mui/material";

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow: hidden;
`;

const FixedHeader = styled.div`
  flex-shrink: 0;
  padding: 26px 10px 0 10px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px 10px 4px 10px;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 30px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 5px 10px;
  }
`;

const FixedFooter = styled.div<{ $colorMode: 'light' | 'dark' }>`
  flex-shrink: 0;
  padding: 4px 0 14px 0;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 0 10px 0;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 14px 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const Title = styled.h1<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 42px;
  line-height: 1.2em;
  color: ${props => props.$colorMode === 'light' ? Colors.black : Colors.white};
  text-align: center;
  margin: 0;
  margin-top: 8px;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 18px;
  line-height: 1.2em;
  color: ${props => props.$colorMode === 'light' ? Colors.grey6 : Colors.grey4};
  text-align: center;
  text-wrap: balance;
  margin: 0 0 10px 0;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 26px;
  max-width: 1200px;
  
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    max-width: 100%;
    padding: 0 10px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 10px;
`;

const TinyText = styled.p<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 12px;
  color: ${Colors.grey5};
  text-align: center;
  text-wrap: balance;
  margin: 8px 0 0 0;
`;

interface PricingProps {
  colorMode?: 'light' | 'dark';
  isOpen: boolean;
  onClose: () => void;
}

const FreeTrial = ({ colorMode = 'dark', isOpen, onClose }: PricingProps) => {
  const config = useConfig();
  const { subscription } = useCreditsContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [selectedCreatorPlan, setSelectedCreatorPlan] = useState(`${CREDITS_CONSTANTS.CREDIT_LIMITS.PAID_CREATOR_LIMIT}`);
  const [selectedPlan, setSelectedPlan] = useState<'hobby' | 'creator'>('hobby');
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Wrapper functions that call the helper functions with the correct parameters
  const redirectToSignup = () => redirectToSignupHelper(navigate, location);
  const onClickHobby = () => onClickHobbyHelper(user, navigate, location, subscription, true);
  const onClickCreator = () => onClickCreatorHelper(user, navigate, location, subscription, true);

  const handleStartFreeTrial = () => {
    if (selectedPlan === 'hobby') {
      onClickHobby();
    } else {
      onClickCreator();
    }
  };

  return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        colorMode={colorMode}
        width="auto"
        height="auto"
        fullscreen={isMobile ? true : false}
        content={
          <ModalContent>
            <FixedHeader>
              <HeaderContainer>
                <Logo size={isMobile ? 30 : 34} colorMode={colorMode} />  {/* text={"right"} caps={true} */}
                <Title $colorMode={colorMode}>
                  Choose a plan
                </Title>
                {!isMobile ? (
                  <Subtitle $colorMode={colorMode}>
                    Start vibe creating your dream games with AI. {/* Out of credits. Choose a plan to keep creating. */}
                  </Subtitle>
                  ) : (
                  <Subtitle $colorMode={colorMode}>
                    Vibe create your dream games.
                  </Subtitle>
                )}
              </HeaderContainer>
            </FixedHeader>
            <ScrollableContent>
              <Section>
                <PricingGrid>
                  <PriceOptionsMini
                    planName="HOBBY"
                    credits={`${CREDITS_CONSTANTS.CREDIT_LIMITS.PAID_HOBBY_LIMIT}`}
                    priceText={getHobbyPriceText(subscription)}
                    selected={selectedPlan === 'hobby'}
                    onSelect={() => setSelectedPlan('hobby')}
                    colorMode={colorMode}
                  />
                  <PriceOptionsMini
                    planName="CREATOR"
                    credits={selectedCreatorPlan}
                    priceText={getCreatorPriceText(subscription)}
                    selected={selectedPlan === 'creator'}
                    onSelect={() => setSelectedPlan('creator')}
                    colorMode={colorMode}
                  />
                </PricingGrid>
              </Section>
            </ScrollableContent>
            <FixedFooter $colorMode={colorMode}>
              <ButtonContainer>
                <Button 
                  text={getModalButtonText(subscription)} 
                  onClick={handleStartFreeTrial}
                  color={colorMode === 'light' ? 'black' : 'white'}
                  size="medium"
                />
                <TinyText $colorMode={colorMode}>
                  Change plans or cancel anytime.
                </TinyText>
              </ButtonContainer>
            </FixedFooter>
          </ModalContent>
        }
      />
  );
};

export default FreeTrial;